import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag, ChevronRight, ShieldCheck, UserRound } from "lucide-react";
import { useCart } from "../lib/cart";
import { useMemberAuth, memberApi } from "../lib/memberAuth";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, setQty, totals, clear } = useCart();
  const { account, idStatus, loading: authLoading } = useMemberAuth();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const r = await memberApi.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "ID_REQUIRED" || detail === "ID_REJECTED") {
        toast.error("Please verify your age (upload your ID) before checking out.");
      } else {
        toast.error("Checkout failed. Please try again.");
      }
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <section className="min-h-screen pt-40 pb-24 px-6 flex items-center justify-center" data-testid="cart-empty">
        <div className="max-w-md text-center">
          <ShoppingBag size={36} strokeWidth={1.2} className="text-[var(--twr-bronze)] mx-auto" />
          <h1 className="font-serif text-4xl mt-6">Your bag is empty.</h1>
          <p className="text-[var(--twr-warm-white)]/60 mt-4">The shop is open. The humidor is full.</p>
          <Link to="/shop" className="btn-bronze mt-10">Enter the Shop <ChevronRight size={14} /></Link>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-32 pb-24 px-6 sm:px-10 lg:px-16" data-testid="cart-page">
      <div className="max-w-[1300px] mx-auto">
        <p className="eyebrow">Your Bag</p>
        <h1 className="font-serif text-5xl sm:text-6xl mt-4">The bag.</h1>

        <div className="grid lg:grid-cols-3 gap-10 mt-14">
          <div className="lg:col-span-2 space-y-4">
            {items.map((i) => (
              <article key={i.id} data-testid={`cart-item-${i.id}`} className="surface p-5 flex items-center gap-5">
                <Link to={`/shop/${i.id}`} className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 overflow-hidden">
                  <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/shop/${i.id}`} className="font-serif text-lg hover:text-[var(--twr-bronze)] transition-colors line-clamp-2">{i.name}</Link>
                  <p className="font-serif text-base text-[var(--twr-bronze)] mt-1">${i.price.toFixed(2)}</p>
                </div>
                <div className="inline-flex items-center border border-[var(--twr-line-strong)]">
                  <button data-testid={`cart-dec-${i.id}`} onClick={() => setQty(i.id, i.quantity - 1)} className="px-3 py-2 hover:text-[var(--twr-bronze)]"><Minus size={12} /></button>
                  <span className="px-4 py-2 text-sm">{i.quantity}</span>
                  <button data-testid={`cart-inc-${i.id}`} onClick={() => setQty(i.id, i.quantity + 1)} className="px-3 py-2 hover:text-[var(--twr-bronze)]"><Plus size={12} /></button>
                </div>
                <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} className="w-10 h-10 inline-flex items-center justify-center border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] transition-colors">
                  <Trash2 size={14} />
                </button>
              </article>
            ))}
            <button onClick={clear} data-testid="cart-clear" className="text-[var(--twr-warm-white)]/55 text-[11px] tracking-[0.28em] uppercase hover:text-[var(--twr-bronze)] mt-2">Clear bag</button>
          </div>

          <aside className="surface p-8 h-fit sticky top-28">
            <p className="eyebrow">Summary</p>
            <div className="mt-6 space-y-3">
              <Row label="Subtotal" value={`$${totals.subtotal.toFixed(2)}`} />
              <Row label="Delivery" value="Calculated next" />
              <Row label="Tax" value="Calculated next" />
            </div>
            <div className="hairline my-6" />
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] tracking-[0.28em] uppercase">Estimated Total</span>
              <span className="font-serif text-2xl text-[var(--twr-bronze)]" data-testid="cart-total">${totals.subtotal.toFixed(2)}</span>
            </div>
            <label className="block mt-8">
              <span className="eyebrow">Age Verification</span>
            </label>
            {authLoading ? null : !account ? (
              <div className="mt-2" data-testid="cart-auth-required">
                <p className="text-[13px] text-[var(--twr-warm-white)]/60 leading-relaxed">
                  Cigar sales require a verified 21+ account. Sign in or create a free account to check out.
                </p>
                <Link to="/account/login?next=/cart" data-testid="cart-signin-btn" className="btn-bronze w-full mt-5 inline-flex justify-center">
                  <UserRound size={14} /> Sign In / Create Account
                </Link>
              </div>
            ) : idStatus === "none" || idStatus === "rejected" ? (
              <div className="mt-2" data-testid="cart-id-required">
                <p className="text-[13px] text-[var(--twr-warm-white)]/60 leading-relaxed">
                  {idStatus === "rejected"
                    ? "Your ID could not be verified. Please upload a new photo to continue."
                    : "One-time step: upload a photo of your ID to confirm you're 21+. It stays on file for future orders."}
                </p>
                <Link to="/account" data-testid="cart-verify-id-btn" className="btn-bronze w-full mt-5 inline-flex justify-center">
                  <ShieldCheck size={14} /> Verify Your Age
                </Link>
              </div>
            ) : (
              <>
                <p className="mt-2 inline-flex items-center gap-2 text-[12px] text-emerald-400" data-testid="cart-id-ok">
                  <ShieldCheck size={13} /> {idStatus === "approved" ? "Verified 21+" : "ID on file — pending review"}
                </p>
                <p className="text-[12px] text-[var(--twr-warm-white)]/45 mt-1">Receipt goes to {account.email}</p>
                <button onClick={checkout} disabled={loading} data-testid="cart-checkout" className="btn-bronze w-full mt-6">
                  {loading ? "Redirecting…" : "Proceed to Checkout"} <ChevronRight size={14} />
                </button>
              </>
            )}
            <p className="text-[11px] text-[var(--twr-warm-white)]/45 mt-4 text-center">Secure payment via Stripe.</p>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-[var(--twr-warm-white)]/65">{label}</span>
      <span>{value}</span>
    </div>
  );
}
